import React from 'react'
// libs
import { Field } from 'redux-form'
import { useRouter } from 'hooks/useRouter'
import { useSnackbar } from 'notistack'
import { useDropzone } from 'react-dropzone'
// icons
import { AsteriskIcon } from 'components/FormElements/components/InputLabel'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// components
import DocLinkField from '../components/DocLinkField'
// hooks
import { useCurrentForm } from 'hooks/risks'
import { useImageUpload, Image } from 'hooks/useImageUpload'
import { useDocLinkValue } from '../useDocLinkValue'
// types
import { Forms } from 'constants/index'
import { AttachmentType, UploadProgress } from '../types'
import { WrappedFieldProps } from 'redux-form/lib/Field'
import { AssetType, FieldType, IField } from 'types/graphql'
// views
import { DocOrLinkWrapper, LabelContainer } from './views'
// utils
import { DROPZONE_ACCEPT, getRejectionMessage } from '../utils'

export { AttachmentType } from '../types'

interface Props extends WrappedFieldProps {
  form?: string
  field: IField
  required: boolean
  isDisabled?: boolean
}

const DocumentOrLink: React.FC<Props> = ({ field, required, input, isDisabled, form = Forms.RISK_TEMPLATE_FORM }) => {
  const router = useRouter()

  const { enqueueSnackbar } = useSnackbar()

  const fileRef = React.useRef<File | null>(null)
  const abortRef = React.useRef<AbortController | null>(null)

  const [upload, setUpload] = React.useState<UploadProgress | null>(null)

  const { fieldLink } = useCurrentForm()
  const { isRisksRegister, riskTemplate } = React.useContext(RiskTemplateAPIContext)

  const { attachment, setValue, clearField, addLink } = useDocLinkValue({ input, form })

  const { uploadFile } = useImageUpload({
    onUpload: (uploadedImage: Image | any) =>
      setValue({ ...uploadedImage, size: fileRef.current?.size, type: AttachmentType.Image }),
    assetType: AssetType.ActivityFile,
    entityPrefix: `entity/${field.id}/assets`
  })

  React.useEffect(() => () => abortRef.current?.abort(), [])

  const parentField = riskTemplate?.fields?.find((item) => item.id === field.parentFieldId)
  const isRegisters = !!(isRisksRegister || router.query.multiple)
  const isTableCell = !!field.parentFieldId && parentField?.fieldType === FieldType.Table
  const showLabel = !isRegisters && !isTableCell
  const isForm = !!fieldLink

  const startUpload = async (file?: File) => {
    if (!file) {
      return
    }

    const controller = new AbortController()

    abortRef.current?.abort()
    abortRef.current = controller
    fileRef.current = file

    setUpload({ name: file.name, size: file.size, progress: 0 })

    await uploadFile(file, {
      signal: controller.signal,
      onProgress: (progress) => setUpload((prev) => prev && { ...prev, progress })
    })

    // a cancelled or replaced upload must not reset the state of the current one
    if (abortRef.current === controller) {
      abortRef.current = null
      setUpload(null)
    }
  }

  const cancelUpload = () => {
    abortRef.current?.abort()
    abortRef.current = null
    setUpload(null)
  }

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: ([file]) => startUpload(file),
    onDropRejected: (rejections) => enqueueSnackbar(getRejectionMessage(rejections), { variant: 'error' }),
    noClick: true,
    multiple: false,
    disabled: isDisabled || isRegisters || !!upload,
    // prevents the outer withDropzone from also catching the drop (nested dropzones)
    noDragEventsBubbling: true,
    accept: DROPZONE_ACCEPT
  })

  // registers show only an existing value, nothing can be added there
  const showField = !isRegisters || !!attachment

  return (
    <DocOrLinkWrapper $isForm={isForm}>
      {showLabel && (
        <LabelContainer>
          {required && <AsteriskIcon />} {field.label}
        </LabelContainer>
      )}

      {showField && (
        <DocLinkField
          compact={isRegisters || isTableCell}
          hideUnderline={isRegisters}
          disabled={isDisabled}
          attachment={attachment}
          upload={upload}
          isDragActive={isDragActive}
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          onBrowse={open}
          onClear={clearField}
          onCancelUpload={cancelUpload}
          onAddLink={addLink}
        />
      )}
    </DocOrLinkWrapper>
  )
}

const FieldComponent = (props) => <Field {...props} component={DocumentOrLink} />

export default FieldComponent
